/**
 * The API stores a signature as a base64 data URL in a `varchar(65535)` column,
 * so an image that is too detailed is rejected outright with "value too long
 * for type character varying(65535)". Everything here exists to guarantee what
 * we send fits, because the failure is invisible until save time.
 */
export const MAX_SIGNATURE_DATA_URL_LENGTH = 65535;

/** Width the exported PNG is normalised to. Wide enough to stay legible when a
 *  document renders it, small enough that a dense scribble still fits budget. */
const TARGET_WIDTH = 1200;

/** Progressively harsher fallbacks, tried in order, if the first export is too
 *  long. A signature drawn with many strokes compresses far worse than a simple
 *  one, so a single fixed size cannot be guaranteed to fit. */
const FALLBACK_WIDTHS = [900, 700, 500, 360];

/**
 * Draws `source` onto an offscreen canvas of the given width, preserving aspect
 * ratio, and returns the PNG data URL. Returns `null` if no 2D context is
 * available (jsdom, or a browser that has lost the context).
 */
const exportAtWidth = (source: HTMLCanvasElement, width: number): string | null => {
  const ratio = source.height / source.width;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width);
  canvas.height = Math.round(width * ratio);

  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;

  // The strokes are anti-aliased dark-on-transparent; smoothing keeps them from
  // breaking up when the backing store is larger than the target.
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL('image/png');
};

/**
 * Exports a canvas as a PNG data URL guaranteed to fit the storage column.
 *
 * Tries the target width first, then progressively smaller ones. Returns `null`
 * when even the smallest fallback is too long, so the caller can report a real
 * error rather than letting the API reject it with a database message.
 */
export const toBoundedDataUrl = (source: HTMLCanvasElement): string | null => {
  for (const width of [TARGET_WIDTH, ...FALLBACK_WIDTHS]) {
    const dataUrl = exportAtWidth(source, width);
    if (dataUrl === null) return null;
    if (dataUrl.length <= MAX_SIGNATURE_DATA_URL_LENGTH) return dataUrl;
  }

  return null;
};
